import { useFF } from './useFeatureFlag'
import { useAuth } from '@/contexts/auth-context'
import {
  PAYWALL_EXP_FLAG_KEY,
  effectivePaywallVariant,
  isPaywallExperimentEligible,
  type PaywallVariant,
} from '../../../convex/paywallShared'

// Client mirror of the server's resolvePaywallVariant. Reads the experiment
// flag (which also records the exposure via useFF) and the current profile from
// auth, then applies the SAME gating as the server (paywallShared.ts) so the
// frontend hard paywall and the backend checkout always agree on the variant.
//
//   variant   — the effective variant ("control" until the flag/profile load,
//               so the UI never blocks before we know the user is hardpaywall99)
//   isHardPaywall — true only once we positively resolve hardpaywall99
//   isLoading — flag or profile still loading
export function usePaywallVariant(): {
  variant: PaywallVariant
  isHardPaywall: boolean
  isLoading: boolean
} {
  const { user, isLoading: authLoading } = useAuth()

  // Enrollment gate: only eligible fresh signups ever read the flag (reading
  // it records the exposure). Ineligible users — pre-existing accounts,
  // subscribers, anonymous — resolve to control WITHOUT entering the
  // experiment pool, mirroring the server's resolvePaywallVariant which
  // short-circuits before bucketing. Eligibility is decided from immutable
  // signup-time facts (createdAt, anonymity) plus subscription state, so a
  // fresh signup who later subscribes keeps their existing assignment (the
  // sessionStorage dedupe and the server's idempotent exposeUser make
  // re-reads harmless).
  const eligible = !authLoading && isPaywallExperimentEligible(user)
  const flagValue = useFF(PAYWALL_EXP_FLAG_KEY, undefined, { skip: !eligible })

  const flagLoading = eligible && flagValue === undefined
  const isLoading = authLoading || flagLoading

  // While anything is still loading, resolve to control (non-blocking). The
  // gate only ever flips to hardpaywall99 once both the flag value and an
  // eligible fresh-signup profile are known.
  const raw = typeof flagValue === 'string' ? flagValue : null
  const variant = isLoading ? 'control' : effectivePaywallVariant(raw, user)

  return { variant, isHardPaywall: variant === 'hardpaywall99', isLoading }
}
